import { TUI_PLATFORM } from '@taiga-ui/cdk';
import { WA_WINDOW } from '@ng-web-apis/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideTaiga } from '@taiga-ui/core';
import {
  ApplicationConfig,
  DOCUMENT,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  NgZone,
  provideAppInitializer,
  signal,
} from '@angular/core';
import {
  provideRouter,
  Router,
  RouteReuseStrategy,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { appRoutes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  AuthInterceptor,
  HttpErrorInterceptor,
  XRequestTypeIdInterceptor,
} from '@core/interceptors';
import { provideServiceWorker } from '@angular/service-worker';
import { LogistTimeManagementClientIdInterceptor } from '@time-management/core/services/logist-time-management-client-id.interceptor';
import { firstValueFrom, take } from 'rxjs';
import {
  TUI_ENGLISH_LANGUAGE,
  TUI_LANGUAGE,
  TUI_RUSSIAN_LANGUAGE,
  TUI_TURKISH_LANGUAGE,
} from '@taiga-ui/i18n';
import { registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';
import { LanguagesWithRegions } from '@core/types/LanguagesWithRegions';
import { ClipboardService } from 'ngx-clipboard';
import {
  CustomRouteReuseStrategy,
  provideAppDarkMode,
  provideWebkitAnchorFallback,
  provideXIconResolver,
} from '@core/other';
import { environment } from '@environment/environment';
import { RouteTrackingService } from '@core/services';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';

import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { AppVersionService } from '@core/services/app-version.service';
import { WA_POSITION_OPTIONS } from '@ng-web-apis/geolocation';
import { AppSessionBootstrapService } from '@core/services/app-session-bootstrap.service';
import { AppErrorHandler } from '@core/services/app-error-handler';

registerLocaleData(localeRu, LanguagesWithRegions.RU_RU, localeRuExtra);

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ErrorHandler,
      useValue: new AppErrorHandler(
        Sentry.createErrorHandler({
          showDialog: true,
        }),
      ),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideTranslateService({
      fallbackLang: 'ru',
      loader: provideTranslateHttpLoader({
        failOnError: true,
        prefix: './assets/i18n/',
        suffix: '.json',
        useHttpBackend: true,
      }),
    }),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      // Angular 22 сменил дефолт на 'always': дочерние маршруты стали получать параметры
      // родителя, и withComponentInputBinding прокидывал :requestNumber во вложенные
      // компоненты (например, в сводку заявки → повторный loadRequest). Возвращаем поведение v21.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
    ),
    importProvidersFrom(BrowserAnimationsModule),
    provideTaiga(),
    provideAppDarkMode(),
    provideWebkitAnchorFallback(),
    // Taiga v5 определяет платформу по user agent и на iOS/Android подключает свои стили контролов
    // (круглые чекбоксы/радио цветом --tui-background-accent-2). В v3 такого не было — фиксируем web.
    { provide: TUI_PLATFORM, useValue: 'web' },
    provideXIconResolver(),
    provideAppInitializer(() => {
      inject(TraceService);
      const initializerFn = ((
        // document: Document,
        // rendererFactory: RendererFactory2,
        outkeepTranslateService: OutkeepTranslateService,
        appVersionService: AppVersionService,
        routeTrackingService: RouteTrackingService,
        appSessionBootstrapService: AppSessionBootstrapService,
      ) => {
        // const renderer = rendererFactory.createRenderer(null, null);

        return () => {
          // injectFeedBackScript(document, renderer); // TODO после ухода со slack стало неактуальным
          routeTrackingService.init();

          if (!isDevMode()) {
            appVersionService
              .loadVersion()
              .pipe(take(1))
              .subscribe(({ version }) => {
                initSentry(version);
              });
          }

          return Promise.all([
            outkeepTranslateService.init(),
            firstValueFrom(appSessionBootstrapService.init$()),
          ]).then(([, user]) => user);
        };
      })(
        // inject(DOCUMENT),
        // inject(RendererFactory2),
        inject(OutkeepTranslateService),
        inject(AppVersionService),
        inject(RouteTrackingService),
        inject(AppSessionBootstrapService),
      );

      return initializerFn();
    }),
    {
      provide: TUI_LANGUAGE,
      useFactory: (outkeepTranslateService: OutkeepTranslateService) => {
        switch (outkeepTranslateService.getLang()) {
          case 'ru':
            return signal(TUI_RUSSIAN_LANGUAGE);
          case 'en':
            return signal(TUI_ENGLISH_LANGUAGE);
          case 'tr':
            return signal(TUI_TURKISH_LANGUAGE);
          default:
            return signal(TUI_RUSSIAN_LANGUAGE);
        }
      },
      deps: [OutkeepTranslateService],
    },
    {
      provide: LOCALE_ID,
      useFactory: (outkeepTranslateService: OutkeepTranslateService) => {
        switch (outkeepTranslateService.getLang()) {
          case 'ru':
            return LanguagesWithRegions.RU_RU;
          case 'en':
            return LanguagesWithRegions.EN_US;
          case 'tr':
            return LanguagesWithRegions.TR_TR;
          default:
            return LanguagesWithRegions.RU_RU;
        }
      },
      deps: [OutkeepTranslateService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LogistTimeManagementClientIdInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: XRequestTypeIdInterceptor,
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
    }),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    {
      provide: ClipboardService,
      useFactory: (ngZone: NgZone, document: Document, window: Window) => {
        const clipboardService = new ClipboardService(ngZone, document, window);

        clipboardService.configure({ cleanUpAfterCopy: true });

        return clipboardService;
      },
      deps: [NgZone, DOCUMENT, WA_WINDOW],
    },
    {
      provide: WA_POSITION_OPTIONS,
      useValue: {
        enableHighAccuracy: true,
        timeout: 30 * 1000,
        maximumAge: 5 * 60 * 1000,
      },
    },
  ],
};

// function injectFeedBackScript(document: Document, renderer: Renderer2) {
//   if (!isDevMode()) {
//     const script = renderer.createElement('script');
//
//     script.src = 'https://tally.so/widgets/embed.js';
//     script.async = true;
//     renderer.appendChild(document.body, script);
//   }
// }

function initSentry(version: string) {
  try {
    Sentry.init({
      dsn: environment.xOutkeepSentryDsn,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
      //eslint-disable-next-line
      tracePropagationTargets: [/^https?:\/\/[^\/]+\/api\/.*$/],
      // replaysSessionSampleRate: 1.0,
      // replaysOnErrorSampleRate: 1.0,
      environment: environment.xOutkeepInfrastructureEnv || 'unknown_env',
      release: version,
      // Use beacon transport to avoid blocking
      transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
      // Disable sending if initialization fails
      enabled: true,
    });
    console.log('Sentry initialized');
  } catch (error) {
    console.warn('Failed to initialize Sentry:', error);
  }
}
